import { useState } from 'react';
import { shortTime } from '../lib/format.ts';

type Bucket = { bucket: string; count: number };

/**
 * Alerts per hour over the last 24 hours.
 *
 * One series, so there is no legend: the panel title names it. One hue, the
 * brand accent, because the encoding is magnitude and nothing else. Every bar
 * carries a hover tooltip and an accessible label, and a hidden table restates
 * the same numbers for screen readers, so the chart is never the only way to
 * read the data.
 */
export function AlertTimeline({ buckets }: { buckets: Bucket[] }) {
  const [hovered, setHovered] = useState<number | null>(null);
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const total = buckets.reduce((sum, b) => sum + b.count, 0);
  const peak = buckets.reduce<Bucket | null>(
    (best, b) => (best === null || b.count > best.count ? b : best),
    null,
  );

  if (buckets.length === 0) {
    return <p className="py-8 text-center text-[13px] text-ink-3">Noch keine Daten.</p>;
  }

  return (
    <figure className="m-0">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <p className="text-[12px] text-ink-2">
          <span className="tnum font-medium text-ink">{total}</span> Alarme in 24 Stunden
        </p>
        {peak && peak.count > 0 ? (
          <p className="text-[12px] text-ink-3">
            Spitze <span className="tnum">{peak.count}</span> um {shortTime(peak.bucket)}
          </p>
        ) : null}
      </div>

      <div
        className="relative flex h-28 items-end gap-[2px]"
        onMouseLeave={() => setHovered(null)}
        role="img"
        aria-label={`Alarme pro Stunde in den letzten 24 Stunden, insgesamt ${total}`}
      >
        {buckets.map((bucket, index) => {
          const ratio = bucket.count / max;
          // Empty hours keep a 2px stub so the axis stays readable as a line.
          const height = bucket.count === 0 ? 2 : Math.max(6, Math.round(ratio * 112));
          const isHovered = hovered === index;

          return (
            <button
              key={bucket.bucket}
              type="button"
              onMouseEnter={() => setHovered(index)}
              onFocus={() => setHovered(index)}
              onBlur={() => setHovered(null)}
              // The hit target spans the full height, the mark does not.
              className="group relative flex h-full flex-1 cursor-default items-end focus:outline-none"
              aria-label={`${shortTime(bucket.bucket)}: ${bucket.count} Alarme`}
            >
              <span
                aria-hidden
                style={{ height: `${height}px` }}
                className={
                  'w-full rounded-t-[4px] transition-[background-color,opacity] duration-150 ' +
                  (bucket.count === 0
                    ? 'bg-line-strong'
                    : isHovered
                      ? 'bg-accent'
                      : 'bg-accent/70 group-hover:bg-accent')
                }
              />
              {isHovered ? (
                <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md border border-line bg-raised px-2 py-1 text-[11px] text-ink shadow-[var(--shadow-pop)]">
                  <span className="tnum font-medium">{bucket.count}</span> um {shortTime(bucket.bucket)}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Absolute times at both ends read as "13:00 to 12:00", which looks like
          time running backwards. Anchor the axis to now instead. */}
      <div className="mt-1.5 flex justify-between border-t border-line pt-1.5 text-[11px] text-ink-3">
        <span>vor 24 Stunden{buckets[0] ? ` (${shortTime(buckets[0].bucket)})` : ''}</span>
        <span>jetzt</span>
      </div>

      <table className="sr-only">
        <caption>Alarme pro Stunde in den letzten 24 Stunden</caption>
        <thead>
          <tr>
            <th scope="col">Stunde</th>
            <th scope="col">Alarme</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket) => (
            <tr key={bucket.bucket}>
              <th scope="row">{shortTime(bucket.bucket)}</th>
              <td>{bucket.count}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
